import React, { useEffect, useState, useRef } from 'react';
import { Dialog, DialogContent, Box, Typography, IconButton, Button, Stack, CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import BankingService from 'services/banking.service';
import { QRCodeSVG } from 'qrcode.react';

interface DynamicQRProps {
  open: boolean;
  onClose: () => void;
  amount: string;
}

const DynamicQR = ({ open, onClose, amount }: DynamicQRProps) => {
  const [emvqrcps, setEmvQrcps] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const bankingService = new BankingService();

  useEffect(() => {
    if (open) {
      setLoading(true);
      setEmvQrcps(null);
      setError(false);

      const startGeneration = async () => {
        try {
          let numericAmount: number;
          if (typeof amount === 'string' && amount.includes(',')) {
            numericAmount = parseFloat(amount.replace(/\./g, '').replace(',', '.'));
          } else {
            numericAmount = parseFloat(amount);
          }

          if (Number.isNaN(numericAmount)) {
            setLoading(false);
            setError(true);
            return;
          }

          const pixKeys = await bankingService.getPixKeys();
          const hasPixKey = pixKeys.pixKeyEvp || pixKeys.pixKeyDoc || pixKeys.pixKeyEmail;

          if (!hasPixKey) {
            await bankingService.requestCreatePixKey({ keyType: 'EVP', key: '' });
          }

          const { id } = await bankingService.createDynamicQRCode(numericAmount);

          const checkQRCode = async () => {
            try {
              const qrCode = await bankingService.getQRCode(id);
              if (qrCode.emvqrcps) {
                setEmvQrcps(qrCode.emvqrcps);
                setLoading(false);
                if (pollingRef.current) clearInterval(pollingRef.current);
              }
            } catch {
              // ignore transient polling errors
            }
          };

          await checkQRCode();
          pollingRef.current = setInterval(checkQRCode, 2000);
        } catch {
          setLoading(false);
          setError(true);
        }
      };

      startGeneration();
    }

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, amount]);

  const renderContent = () => {
    if (loading && !emvqrcps && !error) {
      return (
        <Box display="flex" justifyContent="center" mb={3} minHeight={200} alignItems="center">
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box display="flex" justifyContent="center" mb={3} minHeight={200} alignItems="center">
          <Typography color="error" fontSize={13} textAlign="center">
            Erro ao gerar o QRCode. Feche e tente novamente.
          </Typography>
        </Box>
      );
    }

    if (emvqrcps) {
      return (
        <Box display="flex" justifyContent="center" mb={3} minHeight={200} alignItems="center">
          <QRCodeSVG value={emvqrcps} size={200} />
        </Box>
      );
    }

    return <Box mb={3} minHeight={200} />;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          paddingTop: 2,
        },
      }}
    >
      <DialogContent sx={{ px: 4, pb: 4 }}>
        <Box position="relative" display="flex" alignItems="center" justifyContent="center" mb={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <QrCode2OutlinedIcon sx={{ color: 'primary.main' }} />
            <Typography fontWeight={600} fontSize={16}>
              Receber PIX QRCode
            </Typography>
          </Box>

          <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', right: 0 }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" textAlign="center" mb={3}>
          Gere um QRCode para receber um pagamento
        </Typography>

        <Typography fontWeight={600} textAlign="center" mb={2}>
          QRCode Dinâmico
        </Typography>

        <Typography variant="body2" color="text.secondary" textAlign="center">
          Valor a receber
        </Typography>

        <Typography textAlign="center" fontSize={34} fontWeight={700} color="primary.main" mb={3}>
          R$ {amount}
        </Typography>

        {renderContent()}

        <Stack direction="row" spacing={2} justifyContent="center">
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            disabled={!emvqrcps}
            sx={{
              borderRadius: '999px',
              px: 3,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
            }}
            onClick={() => {
              if (emvqrcps) navigator.clipboard.writeText(emvqrcps);
            }}
          >
            Copiar código PIX
          </Button>

          <Button
            variant="outlined"
            startIcon={<ShareIcon />}
            disabled={!emvqrcps}
            sx={{
              borderRadius: '999px',
              px: 3,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            Compartilhar QRCode
          </Button>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default DynamicQR;
